import { ISelfSignupService } from "../../core/impl/selfSignupServiceImpl";

export type ISelfSignupController = ReturnType<
  typeof SelfSignupControllerFactory
>;

export const SelfSignupControllerFactory = ({
  selfSignupService,
}: {
  selfSignupService: ISelfSignupService;
}) => {
  /**
   * Register the newly confirmed Cognito user in the backend.
   * Returns { user_id, activation_state: "pending_activation" } on success.
   */
  const registerPendingUser = async (payload: {
    first_name?: string;
    last_name?: string;
    phone?: string;
  }): Promise<
    IControllerPromiseResponse<{ user_id: string; activation_state: string }>
  > => {
    try {
      const data = await selfSignupService.registerPendingUser(payload);
      return { success: true, data };
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to register account.";
      return { success: false, error: { message } };
    }
  };

  /**
   * Poll to check whether care-team approval has been granted.
   * Returns { activation_state: "pending_activation" | "active" }.
   * The PendingActivation page calls this every 30 s.
   */
  const checkActivationStatus = async (): Promise<
    IControllerPromiseResponse<{
      user_id: string;
      activation_state: "pending_activation" | "active";
    }>
  > => {
    try {
      const data = await selfSignupService.checkActivationStatus();
      return { success: true, data };
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to check activation status.";
      return { success: false, error: { message } };
    }
  };

  const DEVICE_ACTIVATE_ERRORS: Record<string, string> = {
    DEVICE_NOT_FOUND:
      "This device is not recognised or is already paired to another account. Please check the device ID and try again.",
    USER_NOT_FOUND:
      "Account setup incomplete. Please restart the app and sign up again.",
    SERVER_ERROR: "Something went wrong. Please try again.",
  };

  /**
   * Activate an account by scanning a device QR code.
   * On success returns { activation_state, clinic_id, device_id }.
   * The caller is responsible for persisting clinic_id and navigating.
   */
  const activateDevice = async (
    device_id: string
  ): Promise<
    IControllerPromiseResponse<{
      activation_state: string;
      clinic_id: number;
      device_id: string;
    }>
  > => {
    try {
      const data = await selfSignupService.activateDevice(device_id);

      // API returns error details in the body even on 4xx/5xx
      if (data?.code || data?.activation_state !== "active") {
        const code = data?.code ?? "SERVER_ERROR";
        const message =
          DEVICE_ACTIVATE_ERRORS[code] ??
          data?.message ??
          "Something went wrong. Please try again.";
        return { success: false, error: { message } };
      }

      return {
        success: true,
        data: {
          activation_state: data.activation_state!,
          clinic_id: data.clinic_id!,
          device_id: data.device_id!,
        },
      };
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.";
      return { success: false, error: { message } };
    }
  };

  return {
    registerPendingUser,
    checkActivationStatus,
    activateDevice,
  };
};
